// フォルダのクラウドストレージ階層転送の実行・状態管理
// tasks/plan_folder_bulk_download.md「同名ファイルの上書きと確認ダイアログ」
// 「転送の進捗表示と部分失敗」節・useExportActions.js / useFolderArchive.js の構造に揃える
import { ref, computed } from 'vue';
import _ from 'lodash';

const STATUS_PENDING = 'PENDING';
const STATUS_RUNNING = 'RUNNING';
const STATUS_DONE = 'DONE';
const STATUS_PARTIAL = 'PARTIAL';
const STATUS_FAILED = 'FAILED';

const PROVIDER_LABEL_KEYS = {
  onedrive: 'OneDrive transfer',
  sharepoint: 'SharePoint transfer',
  googledrive: 'Google Drive transfer',
  dropbox: 'Dropbox transfer',
  box: 'Box transfer',
};

// confirm文言(:provider)に埋め込む製品名。ボタン文言(「OneDrive転送」等)とは別物のため分離する
// (ボタン文言をそのまま埋め込むと「OneDriveへ転送します」ではなく「OneDrive転送へ転送します」になる)。
const PROVIDER_DISPLAY_NAMES = {
  onedrive: 'OneDrive',
  sharepoint: 'SharePoint',
  googledrive: 'Google Drive',
  dropbox: 'Dropbox',
  box: 'Box',
};

// sharepointはprovider列を持たず、フォルダ転送APIも既存の単一ファイル転送と同じくonedrive扱い
// (FolderCloudExportController::storeOnedrive がdriveパラメータで振り分けるのみ)。
// そのため状態取得(GET cloud-exports)上はonedriveキーで代表される。
const RESPONSE_PROVIDER_KEY = {
  onedrive: 'onedrive',
  sharepoint: 'onedrive',
  googledrive: 'googledrive',
  dropbox: 'dropbox',
  box: 'box',
};

const ENDPOINT_PROVIDER = {
  onedrive: 'onedrive',
  sharepoint: 'onedrive',
  googledrive: 'googledrive',
  dropbox: 'dropbox',
  box: 'box',
};

export const useFolderCloudExport = () => {
  const exports = ref({});
  const loadingProviders = ref(new Set());
  const errorMessages = ref({});

  const translate = (key, fallback = null) =>
    _.get(window.i18n, key) || fallback || key;

  const providerLabel = (provider) =>
    translate(PROVIDER_LABEL_KEYS[provider] || provider, provider);

  const providerDisplayName = (provider) => PROVIDER_DISPLAY_NAMES[provider] || provider;

  const isLoading = (provider) => loadingProviders.value.has(provider);

  const setLoading = (provider, status) => {
    const next = new Set(loadingProviders.value);
    if (status) {
      next.add(provider);
    } else {
      next.delete(provider);
    }
    loadingProviders.value = next;
  };

  const exportFor = (provider) => exports.value[RESPONSE_PROVIDER_KEY[provider] || provider] || null;

  const isRunning = (provider) => {
    const status = exportFor(provider)?.status;
    return status === STATUS_PENDING || status === STATUS_RUNNING;
  };

  const isDone = (provider) => exportFor(provider)?.status === STATUS_DONE;
  const isPartial = (provider) => exportFor(provider)?.status === STATUS_PARTIAL;
  const isFailed = (provider) => exportFor(provider)?.status === STATUS_FAILED;

  const skippedCount = (provider) => exportFor(provider)?.skipped_count || 0;
  const skippedItems = (provider) => exportFor(provider)?.skipped_items || [];
  const skippedTruncated = (provider) => Boolean(exportFor(provider)?.skipped_truncated);

  const failedCount = (provider) => exportFor(provider)?.failed_files || 0;
  const failedItems = (provider) => exportFor(provider)?.failed_items || [];
  const failedTruncated = (provider) => Boolean(exportFor(provider)?.failed_truncated);

  const errorMessage = (provider) => errorMessages.value[provider] || '';

  const resolveApiErrorMessage = (error, fallbackKey, fallbackText) => {
    const responseMessage = error?.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage !== '') {
      return responseMessage;
    }
    return translate(fallbackKey, fallbackText);
  };

  const fetchLatest = async (folderId) => {
    if (!folderId) {
      exports.value = {};
      return;
    }

    try {
      const response = await window.axios.get(`/api/folders/${folderId}/cloud-exports`);
      exports.value = response.data || {};
    } catch (error) {
      // 状態取得の失敗は画面を壊さない(初期状態のボタンのまま)
      exports.value = {};
    }
  };

  const execute = async (folderId, provider) => {
    if (!folderId || !provider || isLoading(provider)) {
      return;
    }

    setLoading(provider, true);
    errorMessages.value = { ...errorMessages.value, [provider]: '' };

    let estimate = null;
    try {
      const response = await window.axios.get(`/api/folders/${folderId}/download-estimate`);
      estimate = response.data;
    } catch (error) {
      // 見積り取得の失敗で転送自体はブロックしない(件数・容量なしの文言で続行)。
      estimate = null;
    }

    if (estimate && estimate.over_limit) {
      errorMessages.value = {
        ...errorMessages.value,
        [provider]: estimate.message
          || translate(
            'folder.download_size_exceeded',
            '一度にダウンロードできるのは:limitまでです。このフォルダは:sizeあります。サブフォルダごとに分けてダウンロードしてください。'
          ),
      };
      setLoading(provider, false);
      return;
    }

    const confirmMessage = estimate
      ? translate(
          'folder.cloud_export_confirm',
          'このフォルダを:providerへ転送します。対象は:count件・:sizeです。転送先に同じ名前のファイルがある場合は名前を変えて保存します。よろしいですか?'
        )
          .replace(':provider', providerDisplayName(provider))
          .replace(':count', String(estimate.file_count))
          .replace(':size', estimate.total_size_text)
      : translate(
          'folder.cloud_export_confirm_no_size',
          'このフォルダを:providerへ転送します。転送先に同じ名前のファイルがある場合は名前を変えて保存します。よろしいですか?'
        ).replace(':provider', providerDisplayName(provider));

    if (!window.confirm(confirmMessage)) {
      setLoading(provider, false);
      return;
    }

    try {
      const endpointProvider = ENDPOINT_PROVIDER[provider] || provider;
      const body = provider === 'sharepoint' ? { drive: 'sharepoint' } : {};
      const response = await window.axios.post(
        `/api/folders/${folderId}/export-to-${endpointProvider}`,
        body
      );
      exports.value = {
        ...exports.value,
        [RESPONSE_PROVIDER_KEY[provider] || provider]: response.data,
      };
      let alertMessage = translate('Transfer started. We will notify you by email when completed.');
      const retentionDays = response.data && response.data.cleanup_retention_days;
      if (retentionDays) {
        alertMessage += `\n${translate(
          'folder.cloud_export_cleanup_notice',
          '転送先のファイルは、保存後:days日を過ぎると自動削除されます。'
        ).replace(':days', String(retentionDays))}`;
      }
      window.alert(alertMessage);
    } catch (error) {
      errorMessages.value = {
        ...errorMessages.value,
        [provider]: resolveApiErrorMessage(
          error,
          'folder.cloud_export_status_failed',
          '転送に失敗しました。'
        ),
      };
    } finally {
      setLoading(provider, false);
    }
  };

  return {
    exports,
    isLoading,
    isRunning,
    isDone,
    isPartial,
    isFailed,
    skippedCount,
    skippedItems,
    skippedTruncated,
    failedCount,
    failedItems,
    failedTruncated,
    errorMessage,
    fetchLatest,
    execute,
  };
};
